<template>
    <div class="max-w-screen-lg flex flex-col gap-4">
        <page-header>Historical Invoices</page-header>

        <div v-if="loading" class="py-3 text-center">
            <vx-icon-loading class="w-9 h-9" spin></vx-icon-loading>
        </div>

        <template v-else>
            <vx-box
                v-if="invoices.length === 0"
                color="info"
                class="flex flex-col px-8 py-20 gap-6 items-center text-xl text-slate-500"
            >
                There are no historical invoices for this group.
            </vx-box>

            <vx-table v-else class="lg:grid-cols-[repeat(6,auto)]" rowBreakpoint="lg">
                <vx-table-row isHeader data-test="header-row">
                    <div>Number</div>
                    <div>Date of Issue</div>
                    <div>Service Period</div>
                    <div>Total Amount</div>
                    <div>Status</div>
                    <div></div>
                </vx-table-row>

                <vx-table-row v-for="invoice in invoices" :key="invoice.number" data-test="invoice-row">
                    <vx-table-row-title>
                        {{ invoice.number }}
                    </vx-table-row-title>

                    <vx-table-row-item>
                        <template v-slot:label>Date of Issue:</template>
                        {{ formatDate(invoice.issued_at) }}
                    </vx-table-row-item>

                    <vx-table-row-item>
                        <template v-slot:label>Service Period:</template>
                        {{ invoice.service_period ?? '-' }}
                    </vx-table-row-item>

                    <vx-table-row-item>
                        <template v-slot:label>Total Amount:</template>
                        <span class="font-medium tabular-nums" data-test="invoice-amount">
                            {{ currencyCents(invoice.total_cents) }}
                        </span>
                    </vx-table-row-item>

                    <vx-table-row-item>
                        <template v-slot:label>Status:</template>
                        <span
                            class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
                            :class="getStatusClasses(invoice.status)"
                            data-test="status-badge"
                        >
                            {{ formatStatus(invoice.status) }}
                        </span>
                    </vx-table-row-item>

                    <vx-table-row-buttons>
                        <vx-button
                            :href="invoice.download_link"
                            target="_blank"
                            rel="noopener noreferrer"
                            color="muted"
                            outline
                            data-test="download-button"
                        >
                            Download
                            <font-awesome-icon widthAuto :icon="faDownload" />
                        </vx-button>
                    </vx-table-row-buttons>
                </vx-table-row>
            </vx-table>
        </template>
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import {
    VxBox,
    VxIconLoading,
    VxTable,
    VxTableRow,
    VxTableRowTitle,
    VxTableRowItem,
    VxTableRowButtons,
    VxButton,
} from '@voxie/frontend-components';
import { groupBillingClient } from '~/services';
import { currencyCents } from '~/utils/helpers';
import dayjs from '~/utils/dayjs';
import { useToasted } from '~/composables/useToasted';
import { catchRequestError } from '~/utils/request';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faDownload } from '@fortawesome/pro-solid-svg-icons';
import PageHeader from '~/components/general/PageHeader.vue';

const props = defineProps({
    teamId: {
        type: Number,
        required: true,
    },
    groupId: {
        type: String,
        required: true,
    },
});

const toasted = useToasted();

const loading = ref(false);
const invoices = ref([]);

const formatDate = (date) => {
    return dayjs(date).format('MMM D, YYYY');
};

const formatStatus = (status) => {
    const statusMap = {
        paid: 'Paid',
        open: 'Open',
        uncollectible: 'Uncollectible',
    };
    return statusMap[status] || status;
};

const getStatusClasses = (status) => {
    switch (status) {
        case 'paid':
            return 'bg-green-100 text-green-800';
        case 'open':
            return 'bg-yellow-100 text-yellow-800';
        case 'uncollectible':
            return 'bg-red-100 text-red-800';
        default:
            return 'bg-gray-100 text-gray-800';
    }
};

const fetchInvoices = async () => {
    loading.value = true;
    try {
        const response = await groupBillingClient.getInvoices(props.teamId, props.groupId);
        invoices.value = response?.data?.data || [];
    } catch (error) {
        console.error('Failed to fetch invoices:', error);
        toasted.error(catchRequestError(error));
    } finally {
        loading.value = false;
    }
};

onMounted(() => {
    fetchInvoices();
});
</script>
